/* hzo-lade.js — de lade op de telefoon werkt als bij het vorige thema:
 *   het WOORD gaat naar de collectie, het PIJLTJE klapt open.
 *
 * Waarom dit nodig is, per niveau:
 *
 * NIVEAU 1 (Men, Women). Horizon maakt van een onderdeel mét kinderen een
 * <summary>, en een summary heeft geen adres. Je kon dus wel openklappen maar
 * niet naar /collections/men. Het adres staat wél in de markup: in de titelregel
 * binnen het uitklapvak (`.menu-drawer__title`), bedoeld voor de weggeschoven
 * versie die wij niet gebruiken. Daar halen we het op. Een klik op het pijltje
 * laten we met rust, elke andere klik in de rij stuurt door.
 *
 * NIVEAU 2 (Tops, Bottoms). Die zijn met `drawer_accordion: false` gewone links —
 * goed — maar dan staan hun onderdelen altijd open en wordt de lade een sliert.
 * Horizon biedt hier geen tussenweg: accordeon aan maakt er een <summary> van en
 * dan is de link weg. Dus klappen we het derde niveau zelf in, met een eigen
 * rondje ernaast. Zo is élke collectie aan te tikken én blijft het opgeruimd.
 *
 * Alles gedelegeerd op document: Horizon tekent de kop opnieuw in de
 * theme-editor, en luisteraars op de elementen zelf overleven dat niet.
 */
(() => {
  const LADE = '.menu-drawer-container';

  /* ── de knoppen bijzetten ─────────────────────────────────────────────────
     TWEE SOORTEN SUBLIJST, want Horizon bouwt niet altijd hetzelfde.

     Bij een menu van DRIE niveaus maakt hij van niveau 1 een <details> en regelt
     hij dat zelf; wij behandelen dan alleen het derde niveau
     (`--grandchildlist`).

     Bij een menu van TWEE niveaus maakt hij van niveau 1 GEEN uitklapper: er is
     dan geen <details> en geen <summary>, en de sublijst (`--childlist`) staat
     permanent open. Gemeten op Jewett Maine: `details: 0`, `summaries: 0`,
     `grandchildlists: 0`. Zonder deze tak had die winkel dus geen enkele pijl en
     geen enkele lijn — precies de klacht.

     De voorwaarde hieronder houdt ze uit elkaar: een `--childlist` pakken we
     alleen aan als Horizon er zélf geen uitklapper van heeft gemaakt. Anders
     zouden ze op een driedelig menu allebei tegelijk werken. */
  function knoppenZetten() {
    const lijsten = [
      ...document.querySelectorAll(`${LADE} .menu-drawer__menu--grandchildlist`),
      ...[...document.querySelectorAll(`${LADE} .menu-drawer__menu--childlist`)].filter((l) => {
        const li = l.closest('li');
        return li && !li.querySelector(':scope > details, :scope > summary');
      }),
    ];

    lijsten.forEach((lijst) => {
      const rij = lijst.closest('li');
      if (!rij) return;

      /* EEN KLAPVAK OM DE SUBLIJST.
         Een hoogte van nul naar auto animeren kan zonder JavaScript, maar alleen
         met een raster dat van `0fr` naar `1fr` gaat, en dat werkt betrouwbaar
         als het raster ÉÉN rij heeft. Zetten we het raster op de <li> zelf, dan
         zit de link in rij 1 en de lijst in rij 2, en dan blijft die tweede rij
         op nul staan: zonder vrije ruimte om te verdelen valt `1fr` terug op de
         minimumhoogte, en die is nul omdat we `min-height: 0` nodig hebben om
         hem te kunnen dichtklappen. Gemeten, niet aangenomen. */
      if (!lijst.parentElement.classList.contains('hzo-klapvak')) {
        const vak = document.createElement('div');
        vak.className = 'hzo-klapvak';
        lijst.replaceWith(vak);
        vak.appendChild(lijst);
      }

      /* HIER WORDT NIET MEER GEMETEN, EN DAT SCHEELT 621 MS.
       *
       * Er stond: `const hoog = lijst.scrollHeight` — vlak na het verplaatsen
       * van de lijst in een nieuw klapvak. Dat is de klassieke valkuil:
       * schrijven, dan lezen, dan weer schrijven. Elke `scrollHeight` na een
       * DOM-wijziging dwingt de browser het hele menu opnieuw door te rekenen,
       * en dat gebeurde één keer per lijst.
       *
       * Erger nog: deze functie wordt aangeroepen door een waarnemer die naar
       * hetzelfde stuk pagina kijkt waarin hij zelf schrijft, plus bij elke
       * `toggle` en elke klik in de lade. De structuur-wijzigingen zitten achter
       * een slot en gebeuren dus één keer — maar dit meten stond daarbuiten en
       * liep dus élke ronde opnieuw.
       *
       * PageSpeed 17 aug 2026 op de live winkel: `hzo-lade.js` 621 ms
       * processortijd, de tweede post op de hele pagina, met "gedwongen
       * dynamische aanpassing" als aparte melding.
       *
       * WAAROM HET WEG KAN. De hoogte is alleen nodig op het moment dat een rij
       * openklapt, en de klik-afhandelaar hieronder meet hem daar toch al
       * opnieuw ("de inhoud kan sinds het laden veranderd zijn"). Tot die eerste
       * klik geldt de terugval in de opmaak: `max-height: var(--hzo-klaphoogte,
       * 30rem)`. Er verandert dus niets aan wat de bezoeker ziet. */

      if (rij.querySelector(':scope > .hzo-lade-knop')) return;

      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'hzo-lade-knop';
      knop.setAttribute('aria-expanded', 'false');

      const naam = rij.querySelector('.menu-drawer__menu-item-text');
      knop.setAttribute('aria-label', naam ? `Show what is inside ${naam.textContent.trim()}` : 'Show subcategories');

      rij.appendChild(knop);
    });

    /* Het adres van niveau 1 op de rij zetten, zodat de CSS weet dat het woord
       aan te tikken is. */
    document.querySelectorAll(`${LADE} summary.menu-drawer__menu-item--mainlist`).forEach((kop) => {
      if (kop.dataset.hzoNaar) return;
      const link = kop.parentElement?.querySelector('.menu-drawer__title[href]');
      const adres = link && link.getAttribute('href');
      if (adres) kop.dataset.hzoNaar = adres;
    });
  }

  /* DE LADE BESTAAT NOG NIET BIJ HET LADEN. Horizon hangt er
     `data-hydration-key="header-drawer-mobile"` op en vult hem pas als hij nodig
     is. Eén keer bijzetten bij DOMContentLoaded vond dus niets — de knoppen
     bleven weg en er kwam geen foutmelding. Daarom ook bijzetten zodra de lade
     open gaat, en bij elke klik erin.

     `toggle` bubbelt niet, dus in de vangfase meeluisteren. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', knoppenZetten, { once: true });
  } else {
    knoppenZetten();
  }
  document.addEventListener('shopify:section:load', knoppenZetten);
  document.addEventListener('toggle', (e) => {
    if (e.target instanceof Element && e.target.closest(LADE)) knoppenZetten();
  }, true);

  /* WACHTEN TOT HORIZON DE LADE VULT.
     Bovenstaande momenten waren niet genoeg: op een winkel met een menu van twee
     niveaus vult Horizon de lade ná DOMContentLoaded en zonder dat er een
     `toggle` langskomt. Gemeten op Jewett Maine: de sublijsten stonden er wél,
     onze pijltjes niet — en één klik in de lade zette ze alsnog neer. Dat is
     geen gedrag dat je aan een bezoeker kunt uitleggen.

     Een waarnemer dus. MET EEN SLOT, want deze waarnemer kijkt naar hetzelfde
     stuk pagina waarin hij zelf schrijft: zonder afkoppelen voert hij zichzelf
     en legt hij het tabblad plat. Dat is hier eerder gebeurd. Vandaar
     afkoppelen, bijzetten, en pas daarna weer aankoppelen. */
  let waarnemer = null;
  function bewaken() {
    const bak = document.querySelector(LADE);
    if (!bak || waarnemer) return;
    waarnemer = new MutationObserver(() => {
      waarnemer.disconnect();
      knoppenZetten();
      waarnemer.observe(bak, { childList: true, subtree: true });
    });
    waarnemer.observe(bak, { childList: true, subtree: true });
    knoppenZetten();
  }

  /* De bak zelf komt ook pas later; even blijven kijken tot hij er is. */
  if (!document.querySelector(LADE)) {
    const zoeken = new MutationObserver(() => {
      if (document.querySelector(LADE)) { zoeken.disconnect(); bewaken(); }
    });
    zoeken.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => zoeken.disconnect(), 15000);
  } else {
    bewaken();
  }

  /* ── "More" in de kop: TERUGGEDRAAID ────────────────────────────────────
     Hier stond code die Horizons `overflow-list` uitzette en zijn kenmerken
     weghaalde, om More kwijt te raken. Dat is drie keer misgegaan en de derde
     keer sloopte het het menu helemaal: de kop toonde alleen nog het logo.

     Aan een element van Horizon sleutelen dat zelf meet, zichzelf herstelt en
     waarvan de kop de uitkomst uitleest, is dweilen met de kraan open. De
     eigenaar had gelijk: dit hoort een eigen sectie te worden, zoals we dat bij
     de collectiepagina ook gedaan hebben.

     Tot die er is doet Horizon het weer op zijn manier — mét More als het niet
     past. Liever een menu met More dan geen menu.
     ------------------------------------------------------------------------ */

  /* ── klikken ──────────────────────────────────────────────────────────── */
  if (document.documentElement.dataset.hzoLade === 'true') return;
  document.documentElement.dataset.hzoLade = 'true';

  document.addEventListener('click', (e) => {
    /* de lade kan net gevuld zijn; goedkoop genoeg om te herhalen */
    if (e.target.closest?.(LADE)) knoppenZetten();

    /* het rondje van niveau 2 */
    const knop = e.target.closest?.('.hzo-lade-knop');
    if (knop) {
      e.preventDefault();
      const rij = knop.closest('li');
      /* vlak voor het openklappen opnieuw meten: de inhoud kan sinds het laden
         veranderd zijn, en een verkeerde hoogte zie je meteen aan de beweging */
      const vak = rij.querySelector(':scope > .hzo-klapvak');
      const lijst = vak && vak.firstElementChild;
      if (lijst && lijst.scrollHeight) vak.style.setProperty('--hzo-klaphoogte', `${lijst.scrollHeight}px`);
      const wasOpen = rij.hasAttribute('data-hzo-open');
      rij.toggleAttribute('data-hzo-open');
      knop.setAttribute('aria-expanded', String(!wasOpen));
      return;
    }

    /* het woord van niveau 1 */
    const kop = e.target.closest?.(`${LADE} summary.menu-drawer__menu-item--mainlist`);
    if (!kop) return;
    /* HET PIJLTJE BLIJFT DE OPENKLAPPER — op drie manieren, want op één
       manier vertrouwen is hier eerder stukgegaan.

       1. Toetsenbord (`detail === 0`): Enter op een <summary> hoort open te
          klappen, niet weg te navigeren.
       2. Het doelwit zelf. Werkt alleen als het pijltje klikken mág vangen;
          Horizons base.css zet `pointer-events: none` op elk `.svg-wrapper`,
          dus daar staat in onze CSS een regel tegenover.
       3. De plek van de klik. Ook als punt 2 ooit weer omvalt, blijft een tik
          binnen het vak van het pijltje een openklapper. */
    if (e.detail === 0) return;
    if (e.target.closest('.svg-wrapper, .icon-caret')) return;
    const pijlvak = kop.querySelector('.svg-wrapper, .icon-caret');
    if (pijlvak) {
      const r = pijlvak.getBoundingClientRect();
      if (r.width && e.clientX >= r.left && e.clientX <= r.right) return;
    }

    const link = kop.parentElement?.querySelector('.menu-drawer__title[href]');
    if (!link || !link.getAttribute('href')) return;

    /* preventDefault houdt de <details> dicht; anders klapt hij nog open terwijl
       de pagina al aan het laden is en zie je hem opflitsen. */
    e.preventDefault();
    window.location.href = link.href;
  });
})();


/* ── De kop mag niet wegspringen als er een lade opengaat ──────────────────
   Zie het blok "De kop blijft staan als er een lade opengaat" in
   hzo-custom.css: daar staan de meting en de oorzaak.

   Zolang Horizons slot (`scroll-lock`) erop zit zetten we de kop op `fixed` op
   exact de plek waar hij toch al plakte (-1px), en geeft de CSS `.page-wrapper`
   de hoogte van de kop terug als bovenmarge, zodat de rest van de pagina niet
   omhoog schuift nu de kop uit de tekststroom valt.

   De toets `< -1` is het hele punt: alleen ingrijpen als hij ook ECHT plakt.
   Bovenaan de pagina staat de kop onder de aankondigingsbalk op y 51 en is er
   niets aan de hand; hem dan vastzetten zou hem 52 pixels omhoog trekken. */
(() => {
  const html = document.documentElement;
  const zet = () => {
    const kop = document
      .querySelector('header-component.header')
      ?.closest('header.shopify-section');
    if (!kop) return;
    if (html.hasAttribute('scroll-lock')) {
      if (html.hasAttribute('hzo-kop-vast')) return;   // al vast: niet opnieuw meten
      if (kop.getBoundingClientRect().top < -1) html.setAttribute('hzo-kop-vast', '');
    } else {
      html.removeAttribute('hzo-kop-vast');
    }
  };
  new MutationObserver(zet).observe(html, {
    attributes: true,
    attributeFilter: ['scroll-lock'],
  });
  zet();
})();
